import React, { createContext, useContext } from "react";
import { useLocalObservable } from "mobx-react";
import { ScopedCalendarAppointmentMutationDraftStoreRegistry } from "./mutationDraftStore";
import { MutationScope } from "./types";
import { DEFAULT_SCOPE } from "../common";

// ─── Registry Context (app-level, single instance) ───────────────────────────

export const MutationDraftContext =
  createContext<ScopedCalendarAppointmentMutationDraftStoreRegistry | null>(null);
MutationDraftContext.displayName = "MutationDraftContext";

export const MutationDraftProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const registry = useLocalObservable(
    () => new ScopedCalendarAppointmentMutationDraftStoreRegistry(),
  );
  return <MutationDraftContext.Provider value={registry}>{children}</MutationDraftContext.Provider>;
};

// ─── Scope Context (feature-level, declares active scope) ────────────────────

const MutationScopeContext = createContext<MutationScope | null>(null);
MutationScopeContext.displayName = "MutationScopeContext";

/**
 * Provides a MutationScope to all descendants.
 * Wrap a feature boundary (e.g. drawer) once; child hooks read via useMutationScope().
 */
export const MutationScopeProvider: React.FC<React.PropsWithChildren<{ scope: MutationScope }>> = ({
  scope,
  children,
}) => <MutationScopeContext.Provider value={scope}>{children}</MutationScopeContext.Provider>;

/**
 * Read the active mutation scope from context.
 * Must be used within a MutationScopeProvider boundary.
 */
export function useMutationScope(): MutationScope {
  const scope = useContext(MutationScopeContext);
  return (scope ?? DEFAULT_SCOPE) as MutationScope;
}
